import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BE_API_AUTH_URL } from '../constants/app.constants';
import { QrPosterRenderDescriptor } from '../models/qr-poster-render-descriptor.model';
import { LocalizedText, PricingTier, ShipmentDetails, ShippingTier } from '../models/sign-order.models';

export interface AdminOrderListEntry {
  publicId: string;
  orderNumber: string;
  createdAt: string;
  company: { _id: string; name: string };
  requester: { displayName: string; email: string };
  product: { name: { de: string; en: string }; material: string; format: string };
  uniquePropertyCount: number;
  totalCopies: number;
  grossTotalCents: number;
  currency: string;
  status: string;
  paidAt: string | null;
  shippedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
}

export interface AdminOrderListResponse {
  items: AdminOrderListEntry[];
  page: number;
  pageSize: number;
  total: number;
}

export interface AdminOrderDetail {
  publicId: string;
  orderNumber: string;
  createdAt: string;
  updatedAt: string;
  requestLocale: string;
  company: { _id: string; name: string };
  requester: { _id: string; displayName: string; email: string; phone: string };
  productSnapshot: { name: LocalizedText; material: string; format: string } | null;
  pricingSnapshot: AdminOrderPricingSnapshot | null;
  paymentInstructionsSnapshot: any;
  shippingAddressSnapshot: any;
  billingAddressSnapshot: any;
  billingAddressSameAsShipping?: boolean;
  items: AdminOrderItem[];
  baseSignsNetSubtotalCents: number;
  drillingNetSubtotalCents: number;
  signsNetSubtotalCents: number;
  shippingNetCents: number;
  unitWeightGrams: number;
  totalWeightGrams: number;
  taxableNetCents: number;
  vatRateBasisPoints: number;
  vatAmountCents: number;
  grossTotalCents: number;
  currency: string;
  status: string;
  paymentReference: string;
  invoiceNumber: string;
  shipment: ShipmentDetails | null;
  emailEvents: AdminEmailEvent[];
  legalAcceptanceSnapshot: import('../models/sign-order.models').LegalAcceptanceSnapshot | null;
  statusHistory: AdminStatusHistoryEntry[];
  internalNotes: string;
  userVisibleNote: string;
  paidAt: string | null;
  shippedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
}

export interface AdminOrderItem {
  propertyId: string;
  identifier: string;
  address: string;
  copies: number;
  drillingOption: string;
  baseUnitNetCents: number;
  drillingUnitNetCents: number;
  combinedUnitNetCents: number;
  lineNetSubtotalCents: number;
  canonicalRenderSnapshot: CanonicalSignOrderRenderSnapshot | null;
}

export interface AdminOrderPricingSnapshot {
  selectedSignTier: PricingTier;
  selectedShippingTier: ShippingTier;
  totalQuantity: number;
  drillingOption: 'without_drilling' | 'with_drilling';
  baseUnitNetCents: number;
  drillingUnitNetCents: number;
  combinedUnitNetCents: number;
  baseSignsNetSubtotalCents: number;
  drillingNetSubtotalCents: number;
  signsNetSubtotalCents: number;
  shippingNetCents: number;
  unitWeightGrams: number;
  totalWeightGrams: number;
}

export interface CanonicalSignOrderRenderSnapshot {
  descriptor: QrPosterRenderDescriptor;
}

export interface AdminEmailEvent {
  eventType: string;
  recipient: string;
  locale: string;
  status: string;
  attempts: number;
  lastAttemptedAt: string | null;
  lastError: string;
  sentAt: string | null;
  eventKey: string;
}

export interface AdminStatusHistoryEntry {
  previousStatus: string | null;
  nextStatus: string;
  changedAt: string;
  actor: { userId: string; displayName: string; email: string; role: string } | null;
  reason: string;
  reference: string;
  customerVisible: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AdminSignOrderService {
  private readonly base = `${BE_API_AUTH_URL}/admin/sign-orders`;

  constructor(private http: HttpClient) {}

  listOrders(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Observable<AdminOrderListResponse> {
    let httpParams = new HttpParams()
      .set('page', String(params.page || 1))
      .set('pageSize', String(params.pageSize || 20));
    if (params.search) httpParams = httpParams.set('search', params.search);
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.dateFrom) httpParams = httpParams.set('dateFrom', params.dateFrom);
    if (params.dateTo) httpParams = httpParams.set('dateTo', params.dateTo);
    return this.http.get<AdminOrderListResponse>(this.base, { params: httpParams });
  }

  getOrderDetail(publicId: string): Observable<AdminOrderDetail> {
    return this.http.get<AdminOrderDetail>(`${this.base}/${publicId}`);
  }

  retryEmailEvent(publicId: string, eventKey: string): Observable<any> {
    return this.http.post(`${this.base}/${publicId}/retry-email`, { eventKey });
  }
}
